import { forwardRef } from 'preact/compat';
import { RefObject } from 'preact';
import { PositionData, Coords } from '../hooks/UNSAFE_useFloating';

const FloatingTail = forwardRef<HTMLDivElement>(({}, arrowRef) => {
  return (
    <div ref={arrowRef}> {/*Outer arrow - shadow*/}
      <div></div> {/*Inner arrow - body*/}
    </div>
  );
});

function renderTail(
  data: PositionData,
  anchorRef: RefObject<Element | Coords>,
  arrowRef: RefObject<Element>,
  tail: 'none' | 'simple',
  isRtl: boolean
) {
  if (tail === 'simple' && data.arrow) {
    const { x, y, centerOffset } = data.arrow;
    const arrowEl = arrowRef.current as Element;
    const arrowSize = 7;
    const arrowSizeCorner = 6;
    const arrowInnerEl = arrowRef.current?.querySelector('div') as Element;
    const arrowInnerSize = 5;
    const arrowInnerSizeCorner = 5;
    const arrowCommonStyle = {
      position: 'absolute',
      width: '0px',
      height: '0px'
    };
    let anchorRefHeight = 0;
    let anchorRefWidth = 0;
    const current = anchorRef.current;

    // @ts-ignore. Type 'Element | Coords' issues
    // Property 'x' does not exist on type 'Element'.
    // Property 'y' does not exist on type 'Element'.
    const isAnchorRefElement = current?.x === undefined && current?.y === undefined;
    if (isAnchorRefElement) {
      // @ts-ignore. Property 'getBoundingClientRect' does not exist on type 'Coords'.
      anchorRefHeight = Math.round(current.getBoundingClientRect().height);
      // @ts-ignore. Property 'getBoundingClientRect' does not exist on type 'Coords'.
      anchorRefWidth = Math.round(anchorRef.current?.getBoundingClientRect().width);
    }
    const isCornerVerticalPlacement = isAnchorRefElement ?
      Math.abs(centerOffset) >= anchorRefHeight / 2 - arrowSize / 2 :
      ['start-top-corner', 'start-bottom-corner', 'end-top-corner', 'end-bottom-corner'].includes(data.origPlacement);
    const isCornerHorizontalPlacement = isAnchorRefElement ?
      Math.abs(centerOffset) >= anchorRefWidth / 2 - arrowSize / 2 :
      ['top-start-corner', 'top-end-corner', 'bottom-start-corner', 'bottom-end-corner'].includes(data.origPlacement);
    const isCenter = centerOffset === 0;
    const isCenterOffsetPositive = centerOffset > 0;

    // Set styles of the arrow
    setElementStyle(arrowCommonStyle, arrowEl);
    setElementStyle(arrowCommonStyle, arrowInnerEl);

    // Set sizes of the arrow
    setArrowSizes(isCornerVerticalPlacement || isCornerHorizontalPlacement);

    // Corner vs. normal arrow
    function setArrowSizes(isCornerPlacement?: boolean) {
      // Outer arrow (shadow)
      const arrowFinalSize = isCornerPlacement ? arrowSizeCorner : arrowSize;
      setArrowStyle({
        borderTop: `${getSize(arrowFinalSize)} solid rgba(var(--oj-c-PRIVATE-DO-NOT-USE-palette-neutral-rgb-90), 0.1)`,
        borderRight: getTransparentBorder(arrowFinalSize),
        borderLeft: getTransparentBorder(arrowFinalSize)
      });
      // Inner arrow
      const arrowInnerFinalSize = isCornerPlacement ? arrowInnerSizeCorner : arrowInnerSize;
      setArrowInnerStyle({
        borderTop: `${getSize(arrowInnerFinalSize)} solid var(--oj-c-PRIVATE-DO-NOT-USE-popup-bg-color)`,
        borderRight: `${getTransparentBorder(arrowInnerFinalSize)}`,
        borderLeft: `${getTransparentBorder(arrowInnerFinalSize)}`,
      })
    }

    function setElementStyle(style: object, element: Element) {
      //@ts-ignore - regarding types arrowEl.style is not under our control
      Object.assign(element?.style, style);
    }

    function setArrowStyle(style: object) {
      setElementStyle(style, arrowEl);
    }

    function setArrowInnerStyle(style: object) {
      setElementStyle(style, arrowInnerEl);
    }

    function getTurn(eighth: number) {
      return `rotate(${(eighth) * 45}deg)`;
    }

    function getSize(size: number) {
      return `${size}px`;
    }

    function getTransparentBorder(size: number) {
      return `${getSize(size)} solid transparent`;
    }

    const placementArr = data.placement.split('-');
    const side = placementArr[0];

    switch(side) {
      case 'top': setArrowTop(); break;
      case 'end': isRtl ? setArrowStart() : setArrowEnd();break;
      case 'bottom': setArrowBottom(); break;
      case 'start': isRtl ? setArrowEnd() : setArrowStart(); break;
    }

    function setArrowTop() {
      if (x != undefined) {
        if (isCornerHorizontalPlacement) {
          // Top corners
          setArrowStyle({
            top: '', // flip 'reset'
            bottom: getSize(-arrowSize + 2),
            left: getSize(x + (isCenterOffsetPositive ? 5 : -5)),
            transform: getTurn(isCenterOffsetPositive ? 5 : 3)
          });
          setCornerArrowInner();
        } else {
          setArrowStyle({
            top: '', // flip 'reset'
            bottom: getSize(-arrowSize),
            left: isCenter ? getSize(x) : getSize(x + (isCenterOffsetPositive ? 1 : -1)),
            transform: getTurn(0)
          });
          setNonCornerArrowInner();
        }
      }
    }

    function setArrowEnd() {
      if (y != undefined) {
        if (isCornerVerticalPlacement) {
          // End corners
          setArrowStyle({
            top: getSize(y + (isCenterOffsetPositive ? 2 : -2)),
            right: '', // flip 'reset'
            left: getSize(-arrowSize -1),
            transform: getTurn(isCenterOffsetPositive ? 7 : 5)
          });
          setCornerArrowInner();
        } else {
          // End
          setArrowStyle({
            top: isCenter ? getSize(y) : getSize(y + (isCenterOffsetPositive ? -2 : +2)),
            right: '', // flip 'reset'
            left: getSize(-arrowSize * 1.5),
            transform: getTurn(2)
          });
          setNonCornerArrowInner();
        }
      }
    }

    function setArrowBottom() {
      if (x != undefined) {
        if (isCornerHorizontalPlacement) {
          // Bottom corners
          setArrowStyle({
            top: getSize(-arrowSize + 3),
            bottom: '', // flip 'reset'
            left: getSize(x + (isCenterOffsetPositive ? 4 : -4)),
            transform: getTurn(isCenterOffsetPositive ? 2 : 6)
          });
          setArrowInnerStyle({
            top: getSize(-arrowInnerSize),
            left: getSize(-arrowInnerSize + (isCenterOffsetPositive ? 1 : -1))
          });
        } else {
          setArrowStyle({
            top: getSize(-arrowSize),
            bottom: '', // flip 'reset'
            left: isCenter ? getSize(x) : getSize(x + (isCenterOffsetPositive ? 1 : -1)),
            transform: getTurn(4)
          });
          setNonCornerArrowInner();
        }
      }
    }

    function setArrowStart() {
      if (y != undefined) {
        if (isCornerVerticalPlacement) {
          // Start corners
          setArrowStyle({
            top: getSize(y + (isCenterOffsetPositive ? 2 : -2)),
            right: getSize(-arrowSize - 1),
            left: '', // flip 'reset'
            transform: getTurn(isCenterOffsetPositive ? 1 : 3)
          });
          setArrowInnerStyle({
            top: getSize(-arrowInnerSize),
            left: getSize(-arrowInnerSize + (isCenterOffsetPositive ? -2 : 2))
          });
        } else {
          // Start
          setArrowStyle({
            top: isCenter ? getSize(y) : getSize(y + (isCenterOffsetPositive ? -2 : +2)),
            right: getSize(-arrowSize * 1.5),
            left: '', // flip 'reset'
            transform: getTurn(6)
          });
          setNonCornerArrowInner();
        }
      }
    }

    function setCornerArrowInner() {
      setArrowInnerStyle({
        top: getSize(-arrowInnerSize),
        left: getSize(-arrowInnerSize + (isCenterOffsetPositive ? 2 : -2))
      });
    }

    function setNonCornerArrowInner() {
      setArrowInnerStyle({
        top: getSize(-arrowInnerSize - 2),
        left: getSize(-arrowInnerSize)
      });
    }
  }
}

export { FloatingTail, renderTail };
